import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Zap, KeyRound, Bell } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { BlogVisibilitySettings, DubbingSettings } from "./SettingsExtras";
import { clearVisibleBlogs } from "../../lib/visibleBlogs";
import CookiesSettings from "./CookiesSettings";
import MyCloudSettings from "./MyCloudSettings";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TONES = [["formal", "Formal", "Formal"], ["simples", "Simples", "Simple"], ["tecnico", "Técnico", "Technical"], ["amigavel", "Amigável", "Friendly"]];
const LENGTHS = [["curto", "Curto", "Short"], ["medio", "Médio", "Medium"], ["longo", "Longo", "Long"]];

export default function SettingsTab() {
  const { t, lang } = useLang();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <p className="text-muted-foreground">{t("A carregar…", "Loading…")}</p>;

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings", { data: s });
      setS(data); clearVisibleBlogs(); toast.success(t("Configurações guardadas", "Settings saved"));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <h2 className="font-heading text-2xl font-bold">{t("Configurações de Geração", "Generation Settings")}</h2>

      <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-5">
        <h3 className="font-heading font-bold">{t("Predefinições de IA", "AI defaults")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs">{t("Tom por defeito", "Default tone")}</Label>
            <Select value={s.default_tone} onValueChange={(v) => set("default_tone", v)}><SelectTrigger data-testid="set-tone" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs">{t("Tamanho por defeito", "Default length")}</Label>
            <Select value={s.default_length} onValueChange={(v) => set("default_length", v)}><SelectTrigger data-testid="set-length" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{LENGTHS.map(([v, pt, en]) => <SelectItem key={v} value={v}>{lang === "pt" ? pt : en}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <Label className="text-xs">{t("Estilo por defeito", "Default style")}</Label>
            <Input data-testid="set-style" value={s.default_style} onChange={(e) => set("default_style", e.target.value)} className="mt-1.5" />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-3">
        <h3 className="font-heading font-bold flex items-center gap-2"><Zap className="h-4 w-4 text-primary" />{t("Publicação", "Publishing")}</h3>
        <label className="flex items-center justify-between text-sm">
          <span>
            {t("Aprovação automática (admin)", "Auto-approval (admin)")}
            <span className="block text-xs text-muted-foreground">{t("Se ativo, o próprio admin pode publicar mais rápido. Convidados nunca publicam.", "If on, admin can publish faster. Invited users never publish.")}</span>
          </span>
          <Switch data-testid="set-autoapprove" checked={!!s.auto_approve} onCheckedChange={(v) => set("auto_approve", v)} />
        </label>
      </div>

      <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-4">
        <h3 className="font-heading font-bold">{t("Monetização (CTA & Afiliados)", "Monetization (CTA & Affiliates)")}</h3>
        <label className="flex items-center justify-between text-sm">
          <span>{t("Mostrar bloco de destaque (CTA)", "Show featured block (CTA)")}</span>
          <Switch data-testid="set-cta-enabled" checked={!!s.cta_enabled} onCheckedChange={(v) => set("cta_enabled", v)} />
        </label>
        {s.cta_enabled && (
          <div className="grid gap-3">
            <div><Label className="text-xs">{t("Título", "Title")}</Label><Input data-testid="set-cta-title" value={s.cta_title || ""} onChange={(e) => set("cta_title", e.target.value)} className="mt-1.5" /></div>
            <div><Label className="text-xs">{t("Texto", "Text")}</Label><Input data-testid="set-cta-text" value={s.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("Etiqueta do botão", "Button label")}</Label><Input data-testid="set-cta-btn-label" value={s.cta_button_label || ""} onChange={(e) => set("cta_button_label", e.target.value)} className="mt-1.5" /></div>
              <div><Label className="text-xs">{t("URL do botão", "Button URL")}</Label><Input data-testid="set-cta-btn-url" value={s.cta_button_url || ""} onChange={(e) => set("cta_button_url", e.target.value)} className="mt-1.5" placeholder="https://" /></div>
            </div>
          </div>
        )}
        <div className="pt-2 border-t border-white/10">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs">{t("Links de afiliado", "Affiliate links")}</Label>
            <Button size="sm" variant="secondary" data-testid="add-affiliate-btn" onClick={() => set("affiliates", [...(s.affiliates || []), { title: "", url: "" }])}>{t("Adicionar", "Add")}</Button>
          </div>
          <div className="space-y-2">
            {(s.affiliates || []).map((a, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`affiliate-row-${i}`}>
                <Input placeholder={t("Título", "Title")} value={a.title} onChange={(e) => { const arr = [...s.affiliates]; arr[i] = { ...arr[i], title: e.target.value }; set("affiliates", arr); }} />
                <Input placeholder="https://" value={a.url} onChange={(e) => { const arr = [...s.affiliates]; arr[i] = { ...arr[i], url: e.target.value }; set("affiliates", arr); }} />
                <Button size="sm" variant="ghost" className="text-destructive shrink-0" data-testid={`affiliate-remove-${i}`} onClick={() => set("affiliates", s.affiliates.filter((_, j) => j !== i))}>✕</Button>
              </div>
            ))}
            {(s.affiliates || []).length === 0 && <p className="text-xs text-muted-foreground">{t("Sem links. Adiciona quando tiveres os programas de afiliado.", "No links. Add them when you have your affiliate programs.")}</p>}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-card p-6 space-y-4">
        <h3 className="font-heading font-bold flex items-center gap-2"><Bell className="h-4 w-4 text-primary" />{t("Alertas de mensagens", "Message alerts")}</h3>
        <p className="text-sm text-muted-foreground">{t("Recebe um email sempre que chega um contacto, sugestão ou pedido de contribuição.", "Get an email whenever a contact, suggestion or contribution request arrives.")}</p>
        <label className="flex items-center justify-between text-sm">
          <span>{t("Enviar alertas por email", "Send email alerts")}</span>
          <Switch data-testid="set-alert-enabled" checked={s.alert_emails_enabled !== false} onCheckedChange={(v) => set("alert_emails_enabled", v)} />
        </label>
        <div>
          <Label className="text-xs">{t("Email que recebe os alertas", "Alert recipient email")}</Label>
          <Input data-testid="set-alert-email" type="email" value={s.alert_email || ""} onChange={(e) => set("alert_email", e.target.value)} className="mt-1.5" placeholder="admin@email.com" />
        </div>
      </div>

      <BlogVisibilitySettings s={s} set={set} />
      <DubbingSettings s={s} set={set} />
      <CookiesSettings />
      <MyCloudSettings s={s} set={set} />

      <div className="rounded-2xl border border-white/10 bg-card p-6">
        <h3 className="font-heading font-bold flex items-center gap-2"><KeyRound className="h-4 w-4 text-primary" />{t("Integrações", "Integrations")}</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          {t(
            "As gerações de IA (Claude / GPT / Gemini) funcionam sem configuração adicional. A publicação em redes sociais está em modo simulado (MOCKED).",
            "AI generation (Claude / GPT / Gemini) works with no extra setup. Social media publishing is in simulated mode (MOCKED)."
          )}
        </p>
      </div>

      <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="gap-2"><Save className="h-4 w-4" />{saving ? t("A guardar…", "Saving…") : t("Guardar", "Save")}</Button>
    </div>
  );
}
